import store from "@/store";

// same grouping and order on the settings home and in the left menu
export const navGroups = [
  {
    label: "Account",
    groupKey: "account",
    items: [
      {
        slug: "account",
        label: "My Account",
        icon: "user",
        iconType: "settings",
      },
      {
        slug: "billing",
        label: "Billing & Plans",
        icon: "billing",
        iconType: "settings",
      },
    ],
  },
  {
    label: "Sales Channels",
    groupKey: "salesChannels",
    items: [
      {
        slug: "sale-channels",
        label: "Sale Channels",
        icon: "shopping",
        iconType: "settings",
      },
      {
        slug: "events",
        label: "Events",
        icon: "calendar",
      },
      {
        slug: "domains",
        label: "Domains",
        icon: "web",
      },
      {
        slug: "countries",
        label: "Countries",
        icon: "flag",
      },
      {
        slug: "couriers",
        label: "Couriers",
        icon: "truck-time",
      },
      {
        slug: "returns",
        label: "Returns",
        icon: "change",
      },
    ],
  },
  {
    label: "Organization",
    groupKey: "organization",
    items: [
      {
        slug: "companies",
        label: "Companies",
        icon: "briefcase",
        iconType: "settings",
      },
      {
        slug: "warehouses",
        label: "Warehouses",
        icon: "buildings",
      },
      {
        slug: "users",
        label: "Team Members",
        icon: "group",
        iconType: "settings",
      },
    ],
  },
  {
    label: "Tools",
    groupKey: "tools",
    items: [
      {
        slug: "general",
        label: "General",
        icon: "settings",
      },
      {
        slug: "line-options",
        label: "Line Options",
        icon: "tools",
        iconType: "settings",
      },
      {
        slug: "table-views",
        label: "Table Views",
        icon: "menu",
      },
      {
        slug: "labels",
        label: "Labels",
        icon: "barcode",
      },
    ],
  },
  {
    label: "Modules",
    groupKey: "modules",
    items: [
      {
        slug: "products",
        label: "Products",
        icon: "box2",
      },
      {
        slug: "crm",
        label: "CRM",
        icon: "customers",
      },
      {
        slug: "sales-settings",
        label: "Sales",
        icon: "cart",
      },
      {
        slug: "production",
        label: "Production",
        icon: "calendar",
        // always listed: the module is turned on and off on this page
      },
      {
        slug: "problems",
        label: "Problems",
        icon: "warning",
      },
      {
        slug: "customer-service",
        label: "Customer Service",
        icon: "comments-tag",
      },
      {
        slug: "assets",
        label: "Assets",
        icon: "assets",
      },
      {
        slug: "price-comparison",
        label: "Price Comparison",
        icon: "chart-3",
      },
      {
        slug: "income-statement",
        label: "Income Statement",
        icon: "document",
      },
    ],
  },
  {
    label: "AI",
    groupKey: "ai",
    items: [
      {
        slug: "ai",
        label: "AI Settings",
        icon: "ai",
      },
      {
        slug: "ai-workflows",
        label: "AI Workflows",
        icon: "diagram",
      },
      {
        slug: "ai-actions",
        label: "AI Actions",
        icon: "task-list",
      },
      {
        slug: "knowledge-base",
        label: "Knowledge Base",
        icon: "document",
      },
    ],
  },
  {
    label: "Integrations",
    groupKey: "integrations",
    items: [
      {
        slug: "integrations",
        label: "Integrations",
        icon: "puzzle",
        iconType: "settings",
      },
      {
        slug: "api-keys",
        label: "API Keys",
        icon: "shield-security",
      },
    ],
  },
];

// GET endpoint each page needs (see core-backend CheckPermission); no entry = always visible
const PAGE_PERMISSIONS = {
  production: "production/settings",
  billing: "billing/current",
  general: "general-settings",
  "line-options": "line-options/paths",
  "table-views": "table-views",
  collection: "collection-settings",
  packing: "orders/packing-settings",
  problems: "orders/problem-categories",
  crm: "crm/categories",
  products: "products/products/characteristics",
  assets: "custom-rows",
  labels: "label-styles",
  "price-comparison": "price-comparison/webstores",
  "sales-settings": "orders/problems/categories",
  // its settings are the general settings of the tenant
  "delivery-problems": "general-settings",
  "income-statement": "income-statement/categories",
  ai: "ai-settings",
  "ai-workflows": "workflows",
  "ai-actions": "ai-actions",
  "knowledge-base": "knowledge-articles",
  // one page with the categories, the rules and the channels: any of them opens it
  "customer-service": ["customer-service/settings", "channels"],
  companies: "companies",
  warehouses: "warehouses",
  users: "users/roles",
  "sale-channels": "webshops",
  events: "events",
  domains: "domains",
  countries: "countries/details",
  couriers: "couriers",
  returns: "return-settings",
  integrations: "integrations",
  "api-keys": "api-keys",
};

/** Can the user read this API path, the same way the backend decides for a GET request. */
export function canRead(path, permissionIds) {
  const ids = new Set(permissionIds);
  const parts = path.split("/");
  const name = parts.join("::");
  const names = [name, "*", name + "::read"];
  let prefix = parts[0];
  names.push(prefix + "::*::read", prefix + "::*");
  for (const part of parts.slice(1)) {
    prefix += "::" + part;
    names.push(prefix + "::*::read", prefix + "::*");
  }
  return names.some((n) => ids.has(n));
}

/** The user's permissions are loaded (otherwise nothing is decided yet). */
export function permissionsLoaded(user) {
  return Array.isArray(user?.role?.roles_permissions);
}

/** Can this user read the API path (false until the permissions are loaded). */
export function canReadApi(path, user) {
  if (!permissionsLoaded(user)) return false;
  const ids = user.role.roles_permissions.map((p) => p.permission_id);
  return canRead(path, ids);
}

/** The module a page belongs to is on (true while the modules are not loaded: the backend guards the page then). */
function moduleEnabled(slug) {
  const item = navGroups.flatMap((g) => g.items).find((i) => i.slug === slug);
  if (!item || !item.module) return true;
  const modules = store.state.modules;
  if (typeof modules !== "object" || modules === null) return true;
  return modules[item.module] === true;
}

export function canOpenSettingsPage(slug, user) {
  if (!moduleEnabled(slug)) return false;
  const path = PAGE_PERMISSIONS[slug];
  if (!path) return true;
  return [].concat(path).some((p) => canReadApi(p, user));
}

/** Menu groups with the pages the user can open; empty groups are left out. */
export function visibleNavGroups(user) {
  return navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canOpenSettingsPage(item.slug, user)),
    }))
    .filter((group) => group.items.length > 0);
}
